import { Box, BoxProps, Flex, FlexProps } from '@chakra-ui/react';
import { DataTable, DataTableProps } from './DataTable';
import { Pagination } from './pagination/Pagination';
import { ServerError } from '@texas/types';
import { ErrorDetails } from '../alert/ErrorDetails';
import { useColumnVisibility } from './useColumnVisibility';
import { useActiveSession } from '@texas/hooks/useSession';
import { useEffect, useRef, useState } from 'react';
import { dataTableFooterStyles } from '@texas/resources/theme/styles/dataTableFooter';

export interface DataTableContainerProps<TData extends object>
  extends BoxProps {
  error: ServerError | null;
  datatable: DataTableProps<TData>;
  pagination?: DataTablePaginationProps;
  footer?: React.ReactNode;
  footerContainerProps?: FlexProps;
  footerVariant?: 'entityDirectoryBordered';
  showColumnToggle?: boolean;
  tableId?: string;
}

interface DataTablePaginationProps {
  pageSize: number;
  totalItems: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  variant?: string;
}

export function DataTableContainer<TData extends object>(
  props: DataTableContainerProps<TData>,
) {
  const {
    datatable,
    error,
    footer,
    footerContainerProps,
    footerVariant,
    pagination,
    showColumnToggle = true,
    tableId,
    ...containerProps
  } = props;
  const { currentUser } = useActiveSession();
  const footerPositionRef = useRef<HTMLDivElement | null>(null);
  const [isFooterFloating, setIsFooterFloating] = useState(false);
  const footerStyle = footerVariant
    ? dataTableFooterStyles[footerVariant]
    : datatable.variant === 'entity-directory' && (footer || pagination)
    ? dataTableFooterStyles.entityDirectory
    : undefined;

  const [columnVisibility, setColumnVisibility] = useColumnVisibility(
    tableId,
    currentUser.id,
    datatable.columns as any[],
  );

  useEffect(() => {
    const footerPosition = footerPositionRef.current;

    if (!footerPosition) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsFooterFloating(!entry.isIntersecting);
    });

    observer.observe(footerPosition);

    return () => observer.disconnect();
  }, []);

  return (
    <Box {...containerProps}>
      <ErrorDetails error={error} />
      <DataTable
        {...datatable}
        columnVisibility={showColumnToggle ? columnVisibility : undefined}
        onColumnVisibilityChange={
          showColumnToggle ? setColumnVisibility : undefined
        }
      />
      <Box ref={footerPositionRef} h="1px" aria-hidden={true} />
      <Flex
        bg={footerStyle ? undefined : 'inherit'}
        bottom={0}
        boxShadow={
          isFooterFloating ? '0 -0px 32px -12px rgba(0, 0, 0, 0.52)' : 'none'
        }
        justifyContent="space-between"
        mt={footerStyle ? undefined : 2}
        position="sticky"
        sx={footerStyle}
        transition="box-shadow 160ms ease"
        zIndex={2}
        {...footerContainerProps}
      >
        {footer && <>{footer}</>}
        {pagination && (
          <Box ml="auto">
            <Pagination
              totalItems={pagination.totalItems}
              pageSize={pagination.pageSize}
              currentPage={pagination.currentPage}
              onPageChange={pagination.onPageChange}
              variant={pagination.variant}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
}
